/** Volatile, same-running-tab manual intake facts. No credentials or persistence. */
const records = new Map();
const listeners = new Set();
let revision = 0;
const emit = () => { revision += 1; listeners.forEach(listener => listener()); };
export const subscribeManualOrders = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getManualOrdersRevision = () => revision;
export const manualOrderPrincipalKey = binding => {
  const { user, source } = binding || {};
  if (typeof user?.tenant_id !== 'string' || !user.tenant_id.trim()
      || typeof user?.id !== 'string' || !user.id.trim()
      || !['cloud', 'offline'].includes(source)) return null;
  return JSON.stringify([user.tenant_id, user.id, source]);
};
const copy = value => {
  if (Array.isArray(value)) return Object.freeze(value.map(copy));
  if (value && typeof value === 'object') {
    return Object.freeze(Object.fromEntries(Object.entries(value).map(([key, item]) => {
      if (/^(token|authorization|password|credentials|accessToken|refreshToken)$/i.test(key)) {
        throw new Error('Credentials cannot be retained in manual intake work');
      }
      return [key, copy(item)];
    })));
  }
  return value;
};
export const getManualOrderRecord = principalKey => records.get(principalKey) || null;
export const ensureManualOrderRecord = (principalKey, fields) => {
  if (!principalKey) return null;
  if (!records.has(principalKey)) {
    records.set(principalKey, copy({ principalKey, fields, status: 'draft', attempt: null,
      receipt: null, message: '', difference: null }));
  }
  return records.get(principalKey);
};
// Identity comparison also gives synchronous duplicate-click exclusion across
// two mounted consumers and prevents an old completion replacing newer work.
export const replaceManualOrderRecord = (expected, patch) => {
  if (!expected || records.get(expected.principalKey) !== expected) return null;
  const next = copy({ ...expected, ...patch });
  records.set(expected.principalKey, next);
  emit();
  return next;
};

export const manualAcceptanceMessage = result => {
  if (result.outcome === 'saved_locally') {
    return 'Order saved on this computer. Review is required before syncing.';
  }
  if (result.outcome === 'submission_uncertain') {
    return 'The cloud outcome is unknown. The order is saved on this computer for Review; do not create it again.';
  }
  const outcome = result.order?.intake_outcome;
  const checks = outcome?.follow_ons;
  const failed = [checks?.duplicate_warning === 'failed' && 'duplicate warning check',
    checks?.source_registration === 'failed' && 'source registration'].filter(Boolean);
  if (failed.length) return `Order saved. Unfinished: ${failed.join(' and ')}. These steps are not automatically retried.`;
  if (outcome?.replayed) return 'Order already saved. Recovered the original order; follow-on checks were not rechecked.';
  if (outcome?.acceptance !== 'accepted' || checks?.duplicate_warning !== 'completed'
      || checks?.source_registration !== 'completed') {
    return 'Order saved. This server did not report completion of the follow-on checks.';
  }
  return 'Order created successfully.';
};

export const manualFailure = (error, alreadyUncertain = false) => {
  if (error?.localPreservation) return { status: 'uncertain', message: error.localPreservation === 'failed'
    ? 'The cloud outcome is unknown, and saving on this computer could not be confirmed. Keep this tab open and retry the same submission.'
    : 'The cloud outcome is unknown. The account changed before local preservation. Return to the original account to recover this submission.' };
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const numberingRefusal = status === 409 && typeof detail === 'string' && (
    detail === 'Internal order number already exists'
    || /^Daily internal order number limit \(999\) reached for \d{4}-\d{2}-\d{2}$/.test(detail)
  );
  // Only known validation vocabulary is shown. Never echo arbitrary provider
  // exceptions or interpolated customer input (such as an invalid size value).
  const safeRefusals = new Set(['Invalid delivery date format', 'Invalid total price',
    'Internal order number must match the delivery date',
    'Internal order number must use YYYYMMDD-NNN format',
    'Internal order number sequence must be between 001 and 999']);
  let validationMessage = safeRefusals.has(detail) ? detail : null;
  if (typeof detail === 'string' && detail.startsWith('Invalid size:')) {
    validationMessage = 'Choose a valid size from the size list';
  }
  if (typeof detail === 'string' && /^Missing required fields: [a-z_, ]+$/.test(detail)) {
    const labels = { delivery_date: 'Delivery Date', recipient_name: 'Recipient Name',
      recipient_street_address: 'Street Address', recipient_zip_code: 'Zip Code',
      product_description: 'Product Description', total_price: 'Total Price' };
    const missing = detail.slice('Missing required fields: '.length).split(', ');
    if (missing.every(field => labels[field])) validationMessage = `Required: ${missing.map(field => labels[field]).join(', ')}`;
  }
  if (!alreadyUncertain && (error?.manualOutcome === 'not_submitted'
      || numberingRefusal || [400, 401, 403, 404, 422].includes(status))) {
    return { status: 'draft', message: status === 401 || status === 403
      ? 'This account is not authorized to submit. Your form has been retained.'
      : numberingRefusal ? `${detail}. The order was not saved; correct the number or date before trying again.`
      : validationMessage ? `${validationMessage}. The order was not saved; correct the form and try again.`
      : 'The order was not accepted. Check the entered information and try again.' };
  }
  const processing = status === 409 && /progress|processing/i.test(
    typeof detail === 'string' ? detail : String(detail?.code || ''),
  );
  return { status: 'uncertain', message: processing
    ? 'This submission is still processing. Keep this tab open and retry the same submission after a short wait.'
    : 'We could not confirm whether this order was saved. Keep this tab open and retry the same submission; do not create it again.' };
};
